import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem("jenova_cart");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("jenova_cart", JSON.stringify(items));
  }, [items]);

  const add = (product, opts = {}) => {
    const size = opts.size || null;
    const color = opts.color || null;
    const qty = opts.quantity || 1;
    // Prefer explicit effectiveStock from the caller (PDP already resolved per-size). Fall back to product.stock.
    const stock = opts.effectiveStock !== undefined
      ? (opts.effectiveStock == null ? null : Number(opts.effectiveStock))
      : (product.stock == null ? null : Number(product.stock));
    setItems((prev) => {
      const idx = prev.findIndex(
        (i) => i.product_id === product.id && i.size === size && i.color === color
      );
      if (idx >= 0) {
        const next = [...prev];
        const currentQty = next[idx].quantity;
        const wantQty = currentQty + qty;
        const cappedQty = stock != null ? Math.min(wantQty, stock) : wantQty;
        next[idx] = { ...next[idx], quantity: cappedQty, stock };
        return next;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          image: (product.images || [])[0] || "",
          quantity: stock != null ? Math.min(qty, stock) : qty,
          size,
          color,
          stock,
        },
      ];
    });
    setOpen(true);
  };

  const updateQty = (key, qty) => {
    setItems((prev) =>
      prev.map((i) => {
        if (cartKey(i) !== key) return i;
        const ceiling = i.stock != null ? Math.max(0, i.stock) : 50;
        return { ...i, quantity: Math.max(1, Math.min(ceiling, qty)) };
      })
    );
  };

  const remove = (key) => setItems((prev) => prev.filter((i) => cartKey(i) !== key));
  const clear = () => setItems([]);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  const value = useMemo(
    () => ({ items, add, updateQty, remove, clear, subtotal, count, open, setOpen }),
    [items, subtotal, count, open]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);

export function cartKey(item) {
  return `${item.product_id}::${item.size || "_"}::${item.color || "_"}`;
}
